import type { GetInvestorListParams, GetBankAccountsParams } from '../types/investorTypes'

export const investorsQueryKeys = {
  all: ['investor'] as const,

  lists: () => [...investorsQueryKeys.all, 'list'] as const,
  list: (filters: Partial<GetInvestorListParams>) =>
    [...investorsQueryKeys.lists(), filters] as const,

  complianceKycLists: () => [...investorsQueryKeys.all, 'complianceKycList'] as const,
  complianceKycList: (filters: Partial<GetInvestorListParams>) =>
    [...investorsQueryKeys.complianceKycLists(), filters] as const,
  complianceKycDetails: (id: string) => [...investorsQueryKeys.complianceKycLists(), id] as const,

  vaspLists: () => [...investorsQueryKeys.all, 'vaspList'] as const,
  vaspList: (filters: Partial<GetInvestorListParams>) =>
    [...investorsQueryKeys.vaspLists(), filters] as const,
  vaspDetail: (id: string) => [...investorsQueryKeys.vaspLists(), 'detail', id] as const,

  onboardingChecksAll: () => [...investorsQueryKeys.all, 'onboardingChecks'] as const,
  onboardingChecks: (investorId: string) =>
    [...investorsQueryKeys.all, 'onboardingChecks', investorId] as const,

  complianceProfile: (investorId: string) =>
    [...investorsQueryKeys.all, 'complianceProfile', investorId] as const,

  riskProfile: (investorId: string) =>
    [...investorsQueryKeys.all, 'riskProfile', investorId] as const,

  sourceOfFunds: (investorId: string) =>
    [...investorsQueryKeys.all, 'sourceOfFunds', investorId] as const,

  statusCounts: () => [...investorsQueryKeys.all, 'statusCounts'] as const,

  // Per-queue status-counts (sidebar badges + page header)
  kycPendingStatusCounts: () => [...investorsQueryKeys.all, 'kycPending', 'statusCounts'] as const,
  kycExpiredStatusCounts: () => [...investorsQueryKeys.all, 'kycExpired', 'statusCounts'] as const,
  bankVerificationStatusCounts: () =>
    [...investorsQueryKeys.all, 'bankVerification', 'statusCounts'] as const,
  vaspOnboardingStatusCounts: () =>
    [...investorsQueryKeys.all, 'vaspOnboarding', 'statusCounts'] as const,
  // Counts for the 4 VASP queue filter cards (all/pending/active/rejected).
  vaspQueueStatusCounts: () =>
    [...investorsQueryKeys.all, 'vaspQueue', 'statusCounts'] as const,
  rejectedStatusCounts: () => [...investorsQueryKeys.all, 'rejected', 'statusCounts'] as const,
  onboardingPipelineStatusCounts: () =>
    [...investorsQueryKeys.all, 'onboardingPipeline', 'statusCounts'] as const,

  // KYC expired list
  kycExpiredLists: () => [...investorsQueryKeys.all, 'kycExpired', 'list'] as const,
  kycExpiredList: (filters: Partial<GetInvestorListParams>) =>
    [...investorsQueryKeys.kycExpiredLists(), filters] as const,

  // Onboarding pipeline list (with optional step filter)
  onboardingPipelineLists: () => [...investorsQueryKeys.all, 'onboardingPipeline', 'list'] as const,
  onboardingPipelineList: (filters: Partial<GetInvestorListParams> & { step?: string }) =>
    [...investorsQueryKeys.onboardingPipelineLists(), filters] as const,
  // Single pipeline row for one investor — used by the investor-detail
  // Overview + Onboarding drawer. Keyed by email since that's the BE
  // filter we use to find the row.
  onboardingPipelineRow: (email: string) =>
    [...investorsQueryKeys.all, 'onboardingPipeline', 'row', email] as const,

  // Rejected investors list
  rejectedLists: () => [...investorsQueryKeys.all, 'rejected', 'list'] as const,
  rejectedList: (filters: Partial<GetInvestorListParams>) =>
    [...investorsQueryKeys.rejectedLists(), filters] as const,

  // Onboarding review list (investor_status=pending_review + kyc_status=approved + onboarding_status=completed)
  onboardingReviewLists: () => [...investorsQueryKeys.all, 'onboardingReview', 'list'] as const,
  onboardingReviewList: (filters: Partial<GetInvestorListParams>) =>
    [...investorsQueryKeys.onboardingReviewLists(), filters] as const,
  onboardingReviewStatusCounts: () =>
    [...investorsQueryKeys.all, 'onboardingReview', 'statusCounts'] as const,

  details: () => [...investorsQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...investorsQueryKeys.details(), id] as const,

  // ─── Per-investor compliance history feeds (paginated) ────────────────
  // Hierarchical so a single investor's mutation can invalidate every
  // compliance feed for that investor in one call.
  compliance: (investorId: string) =>
    [...investorsQueryKeys.detail(investorId), 'compliance'] as const,

  complianceKyc: (investorId: string) =>
    [...investorsQueryKeys.compliance(investorId), 'kyc'] as const,
  complianceKycPage: (investorId: string, page: number, limit: number) =>
    [...investorsQueryKeys.complianceKyc(investorId), { page, limit }] as const,

  complianceAml: (investorId: string) =>
    [...investorsQueryKeys.compliance(investorId), 'aml'] as const,
  complianceAmlPage: (investorId: string, page: number, limit: number) =>
    [...investorsQueryKeys.complianceAml(investorId), { page, limit }] as const,

  compliancePep: (investorId: string) =>
    [...investorsQueryKeys.compliance(investorId), 'pep'] as const,
  compliancePepPage: (investorId: string, page: number, limit: number) =>
    [...investorsQueryKeys.compliancePep(investorId), { page, limit }] as const,

  complianceActivity: (investorId: string) =>
    [...investorsQueryKeys.compliance(investorId), 'activity'] as const,
  complianceActivityPage: (investorId: string, page: number, limit: number) =>
    [...investorsQueryKeys.complianceActivity(investorId), { page, limit }] as const,
}

export const bankAccountKeys = {
  all: ['bankAccount'] as const,

  lists: () => [...bankAccountKeys.all, 'list'] as const,
  list: (params: GetBankAccountsParams) => [...bankAccountKeys.lists(), params] as const,

  details: () => [...bankAccountKeys.all, 'detail'] as const,
  detail: (id: string) => [...bankAccountKeys.details(), id] as const,
}
